import { prop } from 'ramda';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ManagedPopup } from 'js/components/common/Popup';
import { useOnClose } from 'js/components/common/PopupManager';
import { GameIFrame } from 'js/components/system/Wrapper/components/WrapperInner/components/GameIFrame';
import Category from 'js/components/common/GamesCategory';
import { Game } from 'js/components/common/Game';
import { DISPLAY_MODE, GAME_SIZES } from 'js/components/common/GamesView/constants';
import { useGetRedirectLaunchUrl } from 'js/hooks/gameLauncher';
import { Icon } from 'js/components/common/Icon';
import { useUserSelector } from 'js/store/hooks';
import { useSmartTranslations } from 'js/hooks/i18n';

import style from './CardPopup.scss';

const classNames = {
    header: style.header,
    inner: style.inner,
    close: style.close
};

const layerClassNames = {
    overlay: style.overlay,
    layer: style.layer,
    layout: style.layout
};

const SLIDER_OPTIONS = {
    animation: {
        type: 'monoSlider'
    },
    controls: {
        arrows: { enabled: false },
        bullets: { enabled: false },
        buttons: { enabled: false }
    }
};

export const CardPopup = ({
    ...props
}) => {
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const t = useSmartTranslations();
    const {
        refs: layerRefs,
        addedQueryString,
        launchGameCode,
        launchMode = 1,
        gameCodes = []
    } = props;
    const { layerRef } = layerRefs;
    const titleRef = useRef();
    const [src, setSrc] = useState(null);
    const [isMinimized, setIsMinimized] = useState(false);
    const getRedirectLaunchUrl = useGetRedirectLaunchUrl();
    const refs = useMemo(() => ({
        titleRef
    }), [
        titleRef
    ]);
    const onGameLaunch = useCallback(async ({
        code,
        mode,
        queryString
    }) => {
        const url = await getRedirectLaunchUrl({
            gameCode: code,
            mode,
            additionalQueryString: `${addedQueryString ?? ''}${queryString ?? ''}`
        });

        setSrc(url);
    }, [
        addedQueryString,
        getRedirectLaunchUrl
    ]);

    useEffect(() => {
        launchGameCode && onGameLaunch({ code: launchGameCode, mode: launchMode });
    }, [launchGameCode, onGameLaunch, launchMode]);

    const onClose = useOnClose();
    const onCardClose = useCallback(() => {
        if (layerRef.current.classList.contains(style.minimizing)) {
            onClose();
            return;
        }

        layerRef.current.classList.add(style.closing);
        layerRef.current.classList.remove(style.layer);
        layerRef.current.classList.remove(style.maximizing);
        layerRef.current.classList.remove(style.minimizing);
        layerRef.current.addEventListener('animationend', onClose, { once: true });
        // eslint-disable-next-line no-void
        void layerRef.current.offsetWidth;
        layerRef.current.classList.add(style.layer);
    }, [
        onClose,
        layerRef
    ]);

    const onCardMinimize = useCallback(() => {
        if (isMinimized) {
            return;
        }

        layerRef.current.classList.remove(style.layer);
        layerRef.current.classList.remove(style.maximizing);
        // eslint-disable-next-line no-void
        void layerRef.current.offsetWidth;
        layerRef.current.classList.add(style.minimizing);
        layerRef.current.classList.add(style.layer);

        setIsMinimized(true);
    }, [
        layerRef,
        isMinimized
    ]);

    const onCardMaximize = useCallback(() => {
        if (!isMinimized) {
            return;
        }

        layerRef.current.classList.remove(style.layer);
        layerRef.current.classList.remove(style.minimizing);
        // eslint-disable-next-line no-void
        void layerRef.current.offsetWidth;
        layerRef.current.classList.add(style.maximizing);
        layerRef.current.classList.add(style.layer);

        setIsMinimized(false);
    }, [
        layerRef,
        isMinimized
    ]);

    const onCardToggle = useCallback(() => {
        if (isMinimized) {
            onCardMaximize();
        } else {
            onCardMinimize();
        }
    }, [
        isMinimized,
        onCardMaximize,
        onCardMinimize
    ]);

    const onBack = useCallback(() => {
        setSrc('');
    }, []);

    return (
        <ManagedPopup
            {...props}
            refs={refs}
            className={style.popup}
            classNames={classNames}
            layerClassNames={layerClassNames}
            onClose={onCardClose}
            isModal={false}
        >
            <div className={style.buttons}>
                <Icon
                    type={isMinimized ? 'topArrow' : 'bottomArrow'}
                    onClick={onCardToggle}
                    aria-label={t('playtech.mobile.system.popup.aria-label.minimize')}
                    className={style.minimize}
                />
                {
                    src && !launchGameCode && (
                        <Icon
                            type={'backButton'}
                            onClick={onBack}
                            aria-label={t('playtech.mobile.system.popup.aria-label.close')}
                            className={style.backButton}
                        />
                    )
                }
                <Icon
                    type={'closePopup'}
                    onClick={onClose}
                    aria-label={t('playtech.mobile.system.popup.aria-label.close')}
                />
            </div>
            {src ?
                isLoggedIn ?
                    <GameIFrame src={src} className={style.iframe} /> :
                    <div className={style.message}>
                        {t('playtech.mobile.system.popup.mustBeLoggedIn')}
                    </div>
                : (
                    <Category
                        gameCodes={gameCodes}
                        isDesktop={false}
                        showTitle={false}
                        expandedCategory={true}
                        displayMode={DISPLAY_MODE.GRID}
                        gameSize={GAME_SIZES.MEDIUM}
                        type={null}
                        Game={(args) => (
                            <Game
                                {...args}
                                showFeatureIcons={false}
                                customLaunchGame={onGameLaunch}
                                container="card-popup"
                            />
                        )}
                        showViewMore={false}
                        sliderOptions={SLIDER_OPTIONS}
                        className={style.category}
                    />
                )}
        </ManagedPopup>
    );

};
